import type { ReactNode } from 'react'

import { Button } from '@massilia/ui'
import { Link } from '@tanstack/react-router'

import { PaymentConfirmationLayout } from '@/components/payment-confirmation/payment-confirmation-layout'
import { formatPrice } from '@/utils/format-price'

interface SimplePaymentConfirmationProps {
  amountCents?: number
  /** Extra action buttons rendered in the action row, after "Mon espace client" and "Réessayer" */
  extraActions?: ReactNode
  reference?: string
  retryLink?: string
  status: 'failed' | 'success' | 'unknown'
  strings: {
    failedDescription: string
    failedTitle: string
    successDescriptionBuilder: (amountLabel?: string, referenceLabel?: string) => string
    successTitle: string
    unknownDescription: string
    unknownTitle: string
  }
}

export function SimplePaymentConfirmation({
  amountCents,
  extraActions,
  reference,
  retryLink,
  status,
  strings,
}: SimplePaymentConfirmationProps) {
  const isSuccess = status === 'success'
  const isFailed = status === 'failed'

  const amountLabel = amountCents !== undefined ? formatPrice(amountCents / 100) : undefined
  const referenceLabel = reference ? ` pour le dossier ${reference}` : ''

  const variant = isSuccess
    ? {
        iconBackgroundClassName: 'bg-brand-100',
        description: strings.successDescriptionBuilder(amountLabel, referenceLabel),
        iconType: 'check' as const,
        title: strings.successTitle,
      }
    : isFailed
      ? {
          iconBackgroundClassName: 'bg-error-50',
          description: strings.failedDescription,
          iconType: 'x' as const,
          title: strings.failedTitle,
        }
      : {
          iconBackgroundClassName: 'bg-gray-light-100',
          description: strings.unknownDescription,
          iconType: 'x' as const,
          title: strings.unknownTitle,
        }

  return (
    <PaymentConfirmationLayout variant={variant}>
      <div className="border-gray-light-200 flex flex-col gap-3 rounded-2xl border bg-white p-6">
        <div className="text-gray-light-900 text-2xl font-semibold">{variant.title}</div>
        <p className="text-gray-light-700 text-sm">{variant.description}</p>
        {reference && (
          <div className="text-gray-light-700 text-sm">
            Numéro de dossier : <span className="font-semibold">{reference}</span>
          </div>
        )}

        <div className="mt-2 flex flex-wrap gap-3">
          <Link to="/">
            <Button type="button">Mon espace client</Button>
          </Link>
          {isFailed && retryLink && (
            <Link to={retryLink}>
              <Button type="button" variant="outline">
                Réessayer
              </Button>
            </Link>
          )}
          {isSuccess && extraActions}
        </div>
      </div>
    </PaymentConfirmationLayout>
  )
}
